import { isRetryableApiError } from '@/helpers/notify'
import { CAPACITY_FIELD, DESCRIPTION_FIELD } from './constants'

/**
 * SCRUM-1032 QC round 7 — which failed pipe writes are worth a second try.
 *
 * The retry exists for the 500s this API returns under concurrent pipe writes,
 * and for a dropped connection. A 400 is the server refusing the value itself:
 * sending it again gets the same answer, and every attempt raised another
 * toast ("about 3 per sec", Wei Woon, 21 Oct).
 */
export const isRetryableSaveError = (error: unknown): boolean =>
  isRetryableApiError(error)

/** Why a pipe cannot be written yet. */
export type PipeProblem = 'descriptionMissing' | 'capacityMissing' | 'capacityNegative'

interface PipeValues {
  id: number | string
  description?: unknown
  capacity?: unknown
}

const capacityProblem = (capacity: unknown): PipeProblem | null => {
  if (capacity === null || capacity === undefined || capacity === '') {
    return 'capacityMissing'
  }
  const value = Number(capacity)
  if (!Number.isFinite(value)) return 'capacityMissing'
  // The server's rule is `>= 0`; 0 is a pipe with no stated capacity.
  return value < 0 ? 'capacityNegative' : null
}

/**
 * The cells the server would refuse, keyed `${id}::${field}`, with the reason.
 *
 * One rule for staged pipes and for edits to existing ones. Edits used to go
 * out unchecked, so a capacity of -10 or an emptied name reached the API and
 * came back as a 400 (SCRUM-1032 QC round 7, items 1 and 3).
 */
export const findPipeProblems = (
  pipes: PipeValues[],
): Map<string, PipeProblem> => {
  const problems = new Map<string, PipeProblem>()
  pipes.forEach((pipe) => {
    if (String(pipe.description ?? '').trim() === '') {
      problems.set(`${pipe.id}::${DESCRIPTION_FIELD}`, 'descriptionMissing')
    }
    const capacity = capacityProblem(pipe.capacity)
    if (capacity) problems.set(`${pipe.id}::${CAPACITY_FIELD}`, capacity)
  })
  return problems
}

/**
 * True when every problem is a value not typed yet, so the row is still being
 * filled in and the pause autosave can wait for it without a word.
 *
 * A negative capacity is not that: it is a finished value the server will
 * refuse. Held silently with the rest, -10 on a new pipe said nothing until
 * Next (SCRUM-1032 QC round 7 follow-up).
 */
const NOT_TYPED_YET: ReadonlySet<PipeProblem> = new Set<PipeProblem>([
  'descriptionMissing',
  'capacityMissing',
])

export const onlyMissingValues = (problems: Map<string, PipeProblem>): boolean =>
  Array.from(problems.values()).every((problem) => NOT_TYPED_YET.has(problem))

/** The row id in a `${rowId}::${field}` cell key. */
export const cellRowId = (key: string): string => key.split('::')[0]

/**
 * The cells to mark after a save's check.
 *
 * Without held new pipes the check saw every row, so its marks replace the
 * old ones. With new pipes held back (the pause autosave), those pipes were
 * not checked: their marks from an explicit Save stay, while marks on
 * existing pipes are replaced by this check's — so a pipe fixed while a blank
 * new one was being typed no longer stayed red, and a negative new pipe no
 * longer wiped the other new pipes' marks (SCRUM-1032 review).
 */
export const nextInvalidCells = (
  previous: ReadonlySet<string>,
  marks: Iterable<string>,
  holdingDrafts: boolean,
  isDraftRowId: (rowId: string) => boolean,
): Set<string> => {
  if (!holdingDrafts) return new Set(marks)
  const keptDraftMarks = Array.from(previous).filter((key) =>
    isDraftRowId(cellRowId(key)),
  )
  return new Set([...keptDraftMarks, ...marks])
}

/**
 * The capacity a draft holds after an edit. 0 is a real capacity (the server
 * accepts `>= 0`); only a blank or unreadable value means "not set yet". A
 * negative value is kept so the check above can name it, rather than being
 * dropped as if it had never been typed.
 */
export const toDraftCapacity = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * A pipe's id and the ids of every pipe under it, as strings. Deleting a
 * mainline removes its sub-pipes with it, so their staged edits go too.
 */
export const collectPipeAndDescendants = (
  id: number,
  pipes: { id?: unknown; parentId?: unknown }[],
): Set<string> => {
  const gone = new Set<string>([String(id)])
  // Breadth-first from the deleted pipe; bounded because each id is queued
  // at most once.
  const queue = [String(id)]
  while (queue.length > 0) {
    const parent = queue.shift()
    for (const pipe of pipes) {
      const pipeId = String(pipe.id)
      if (!gone.has(pipeId) && String(pipe.parentId) === parent) {
        gone.add(pipeId)
        queue.push(pipeId)
      }
    }
  }
  return gone
}

/**
 * The staged drafts left once the ones under `goneIds` are dropped, cascading
 * through drafts staged under those. With `includeGone`, a draft whose own id
 * is in `goneIds` goes too (discarding a draft); without it only what hangs
 * under them does (a deleted existing pipe, which is not a draft).
 */
export const dropDraftsUnder = <T extends { id: number; parentId: number }>(
  drafts: T[],
  goneIds: ReadonlySet<string>,
  includeGone = false,
): T[] => {
  const doomed = new Set<string>(goneIds)
  const dropped = new Set<number>()
  if (includeGone) {
    drafts.forEach((draft) => {
      if (goneIds.has(String(draft.id))) dropped.add(draft.id)
    })
  }
  // Repeated until nothing more falls: a draft can be listed before the
  // draft it hangs under. Bounded, since each pass drops at least one.
  let changed = true
  while (changed) {
    changed = false
    for (const draft of drafts) {
      if (!dropped.has(draft.id) && doomed.has(String(draft.parentId))) {
        dropped.add(draft.id)
        doomed.add(String(draft.id))
        changed = true
      }
    }
  }
  return dropped.size === 0 ? drafts : drafts.filter((draft) => !dropped.has(draft.id))
}

interface NestedPipe {
  id?: unknown
  subPipes?: NestedPipe[] | null
}

/**
 * How many pipes sit under pipe `id` in the nested list the API returns, at
 * any depth. The delete takes them all, and the dialog should say so (D2
 * retest: two sub-pipes went with no mention). Bounded: each node is visited
 * once.
 */
export const countSubPipes = (id: unknown, pipes: NestedPipe[]): number => {
  const countUnder = (nodes: NestedPipe[] | null | undefined): number =>
    (nodes ?? []).reduce((sum, node) => sum + 1 + countUnder(node.subPipes), 0)
  const stack = [...pipes]
  while (stack.length > 0) {
    const node = stack.pop() as NestedPipe
    if (String(node.id) === String(id)) return countUnder(node.subPipes)
    stack.push(...(node.subPipes ?? []))
  }
  return 0
}
